import React from 'react';
import { Navigate, Route, Routes } from 'react-router';

import ErrorPage from '@dyl/pages/ErrorPage';
import UnAuthorizedError from '@dyl/pages/ErrorPage/unAuthorizedError';
import MediaList from '@dyl/pages/Medias/list';
import RubricEdit from '@dyl/pages/Rubrics/edit';
import RubricList from '@dyl/pages/Rubrics/list';
import TaskList from '@dyl/pages/Tasks/list';
import TokenList from '@dyl/pages/Tokens/list';
import TokensGenerate from '@dyl/pages/Tokens/generate';
import TaskEditorV2 from '@dyl/pages/Tasks/edit';
import NewTest from "@dyl/pages/Tests/NewTest";
import EditTest from "@dyl/pages/Tests/EditTest";
import TestList from '@dyl/pages/Tests/list';
import AnchorTaskList from "@dyl/pages/AnchorTasks";
import ComponentList from '@dyl/pages/TestComponents/list';
import NewComponent from '@dyl/pages/TestComponents/NewComponent';
import EditComponent from '@dyl/pages/TestComponents/EditComponent';
import AutomarkerTargetTemplatesList from '@dyl/pages/AutoMarkerTargetTemplates/list';
import AutomarkerTargetTemplateEditor from '@dyl/pages/AutoMarkerTargetTemplates/edit';
import { useSettings } from '@dyl/systems/SettingsManager';
import { useRoles } from '@dyl/systems/RolesContext';
import LevelSelect from "@dyl/pages/LevelSelect";
import WelcomePage from "@dyl/pages/WelcomePage";
import { useLevels } from "@dyl/systems/LevelsManager";
import WritingAutomarkerAssess from "@dyl/pages/WritingAutomarkerAssess";
import CommonMisspellings from '@dyl/pages/CommonMisspellings';
import RoleAuthCheck from './roleAuthCheck';


export const TESTS_BASE_PATH = '/:level_label/tests';
export const TASKS_BASE_PATH = '/:level_label/tasks';
export const RUBRICS_BASE_PATH = '/:level_label/rubrics';
export const COMPONENTS_BASE_PATH = '/:level_label/components';


export default function AppRoutes() {
    const { globalLevel } = useLevels();
    const { startupPath } = useSettings();
    const { roleNames } = useRoles();

    return (
        <Routes>
            {/* Welcome Page */}
            <Route element={<RoleAuthCheck allowedRoles={['*']} userRoleNames={roleNames} />}>
                <Route path='/' element={<Navigate to={startupPath} />} />
                <Route path=':level_label/welcome' element={<WelcomePage />} />
            </Route>

            {/* Level Selection */}
            <Route element={<RoleAuthCheck allowedRoles={['*']} userRoleNames={roleNames} />}>
                <Route path='/level_select' element={<LevelSelect />} />
            </Route>

            {/* Tests */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path={TESTS_BASE_PATH} element={<TestList />} />
                <Route path={`${TESTS_BASE_PATH}/new`} element={<NewTest />} />
                <Route path={`${TESTS_BASE_PATH}/:testId`} element={<EditTest />} />
            </Route>

            {/* Components */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path={COMPONENTS_BASE_PATH} element={<ComponentList />} />
                <Route path={`${COMPONENTS_BASE_PATH}/new`} element={<NewComponent />} />
                <Route path={`${COMPONENTS_BASE_PATH}/:skill/:componentId`} element={<EditComponent />} />
            </Route>

            {/* Tasks */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path={TASKS_BASE_PATH} element={<TaskList />} />
                <Route path={`${TASKS_BASE_PATH}/anchor`} element={<AnchorTaskList />} />
                <Route path={`${TASKS_BASE_PATH}/:taskType/new`} element={<TaskEditorV2 />} />
                <Route path={`${TASKS_BASE_PATH}/:taskType/:taskId`} element={<TaskEditorV2 />} />
            </Route>

            {/* Rubrics */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path={RUBRICS_BASE_PATH} element={<RubricList />} />
                <Route path={`${RUBRICS_BASE_PATH}/:taskType/new`} element={<RubricEdit />} />
                <Route path={`${RUBRICS_BASE_PATH}/:taskType/:rubricId`} element={<RubricEdit />} />
            </Route>

            {/* Tokens */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT', 'DYLE_SUPPORT']} userRoleNames={roleNames}/>}>
                <Route path=':level_label/passwords' element={<TokenList />} />
            </Route>
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT', 'DYLE_SUPPORT']} userRoleNames={roleNames}/>}>
                <Route path=':level_label/passwords/generate' element={<TokensGenerate />} />
            </Route>
            {/* The following pages are level independent yet utilize the same URL schema */}

            {/* Audios & Images */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path=':level_label/medias' element={<MediaList />} />
            </Route>

            {/* Automarker Target Templates */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path=':level_label/amtt' element={<AutomarkerTargetTemplatesList />} />
                <Route path=':level_label/amtt/new' element={<AutomarkerTargetTemplateEditor />} />
                <Route path=':level_label/amtt/:templateId' element={<AutomarkerTargetTemplateEditor />} />
            </Route>

            {/* Unlinked pages */}
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                <Route path='writing_automarker/assess' element={<WritingAutomarkerAssess />} />
            </Route>
            <Route element={<RoleAuthCheck allowedRoles={['DYLE_ADMIN', 'DYLE_CONTENT']} userRoleNames={roleNames}/>}>
                {(globalLevel.label === 'A1' || globalLevel.label === 'A2') && <Route path=':level_label/misspelling-words' element={<CommonMisspellings />} />}
            </Route>

            {/* 404 page */}
            <Route path='*' element={<ErrorPage />} />

            {/* 401 UNAUTHORIZED */}
            <Route path='/unauthorized' element={<UnAuthorizedError />} />
        </Routes>
    );
}